import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown, Form, Grid, Header, Input, Message, Modal } from 'semantic-ui-react';

import Button from '../../../components/Button';
import { AddressService } from '../../../services/address';
import { getUsers } from '../../../services/estate';
import {
  maskCpf,
  maskPhoneNumber,
  maskZipCode,
  unmaskCpfOrCnpj,
  unmaskPhoneNumber
} from '../../../services/masks';
import { dangerNotification } from '../../../services/notification';
import { isValidEmail } from '../../../utils/emailValidator';
import { UserState } from '../../../types/UserState';

import { initialCreateCommissioningCollaboratorForm } from '../constants';
import {
  CreateCommissioningCollaboratorFormErrors,
  CreateCommissioningCollaboratorFormState
} from '../types';

type Props = {
  open: boolean;
  isBrokerAdmin: boolean;
  isEstateAdmin: boolean;
  defaultEstateId: string;
  estateOptions: Array<{ key: string; text: string; value: string }>;
  saving: boolean;
  onClose: () => void;
  onSubmit: (form: CreateCommissioningCollaboratorFormState) => void;
};

type DropdownOption = {
  key: string | number;
  text: string;
  value: string;
};

const requiredFields: Array<keyof CreateCommissioningCollaboratorFormState> = [
  'name',
  'email',
  'cpf',
  'birthdate',
  'monthlyIncome',
  'professionalOccupation',
  'phone',
  'street',
  'streetNumber',
  'neighborhood',
  'city',
  'state',
  'zipCode',
  'bankCode',
  'bankBranchNumber',
  'bankAccountNumber',
  'bankAccountCheckDigit'
];

const validateForm = (
  form: CreateCommissioningCollaboratorFormState,
  isBrokerAdmin: boolean
): CreateCommissioningCollaboratorFormErrors => {
  const errors: CreateCommissioningCollaboratorFormErrors = {};

  if (isBrokerAdmin && !form.estateId) {
    errors.estateId = 'Selecione uma imobiliária.';
  }

  requiredFields.forEach(field => {
    const value = form[field];
    if (!String(value || '').trim()) {
      errors[field] = 'Campo obrigatório.';
    }
  });

  if (form.email && !isValidEmail(form.email)) {
    errors.email = 'Informe um e-mail válido.';
  }

  if (unmaskCpfOrCnpj(form.cpf).length !== 11) {
    errors.cpf = 'Informe um CPF válido.';
  }

  if (form.phone && unmaskPhoneNumber(form.phone).length < 10) {
    errors.phone = 'Informe um celular válido.';
  }

  if (form.bankCode && form.bankCode.length > 3) {
    errors.bankCode = 'Selecione um banco válido.';
  }

  if (form.zipCode.replace(/\D/g, '').length !== 8) {
    errors.zipCode = 'Informe um CEP válido.';
  }

  if (form.state.trim().length !== 2) {
    errors.state = 'Informe a UF com 2 letras.';
  }

  if (Number(form.monthlyIncome) <= 0) {
    errors.monthlyIncome = 'Informe uma renda válida.';
  }

  return errors;
};

const getUserDocument = (user: UserState) =>
  String(user.document || (user as UserState & { doc?: string }).doc || '');

const getUserPhone = (user: UserState) =>
  String(user.cellphone || user.phone || '');

const toUserOptions = (users: UserState[]): DropdownOption[] =>
  users.map((user, index) => ({
    key: user.id || index,
    text: user.name || user.email || 'Usuário sem nome',
    value: user.id
  }));

const CreateCollaboratorModal: React.FC<Props> = ({
  open,
  isBrokerAdmin,
  isEstateAdmin,
  defaultEstateId,
  estateOptions,
  saving,
  onClose,
  onSubmit
}) => {
  const [form, setForm] = useState<CreateCommissioningCollaboratorFormState>(
    initialCreateCommissioningCollaboratorForm
  );
  const [errors, setErrors] = useState<CreateCommissioningCollaboratorFormErrors>({});
  const [userOptions, setUserOptions] = useState<DropdownOption[]>([]);
  const [users, setUsers] = useState<UserState[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [addressLookupLoading, setAddressLookupLoading] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState<string | null>(null);

  const selectedEstateId = form.estateId || defaultEstateId;

  const canPickEstate = isBrokerAdmin;
  const shouldLoadUsers = Boolean(selectedEstateId) && (isBrokerAdmin || isEstateAdmin);

  const modalInitialForm = useMemo(
    () => ({
      ...initialCreateCommissioningCollaboratorForm,
      estateId: isEstateAdmin ? defaultEstateId : ''
    }),
    [defaultEstateId, isEstateAdmin]
  );

  useEffect(() => {
    if (!open) return;
    setForm(modalInitialForm);
    setErrors({});
    setUserOptions([]);
    setUsers([]);
    setAddressLookupError(null);
  }, [modalInitialForm, open]);

  useEffect(() => {
    if (!open || !shouldLoadUsers) return;

    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        const usersResponse = await getUsers(selectedEstateId);
        setUsers(usersResponse || []);
        setUserOptions(toUserOptions(usersResponse || []));
      } catch (error) {
        setUsers([]);
        setUserOptions([]);
        dangerNotification('Erro', 'Não foi possível carregar usuários da imobiliária.');
      } finally {
        setUsersLoading(false);
      }
    };

    loadUsers();
  }, [open, selectedEstateId, shouldLoadUsers]);

  const updateField = (
    field: keyof CreateCommissioningCollaboratorFormState,
    value: string
  ) => {
    if (field === 'estateId') {
      setUserOptions([]);
      setUsers([]);
    }

    setForm(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'estateId' ? { userId: '' } : {})
    }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const applySelectedUser = (userId: string) => {
    const selectedUser = users.find(user => user.id === userId);

    setForm(prev => ({
      ...prev,
      userId,
      name: selectedUser?.name || prev.name,
      email: selectedUser?.email || prev.email,
      cpf: selectedUser ? maskCpf(getUserDocument(selectedUser)) : prev.cpf,
      phone: selectedUser ? maskPhoneNumber(getUserPhone(selectedUser)) : prev.phone
    }));
    setErrors(prev => ({ ...prev, userId: undefined }));
  };

  const handleZipCodeLookup = async (value: string) => {
    const masked = maskZipCode(value);
    updateField('zipCode', masked);
    const cleanZip = masked.replace(/\D/g, '');

    if (cleanZip.length !== 8) {
      setAddressLookupError(null);
      return;
    }

    setAddressLookupLoading(true);
    setAddressLookupError(null);
    try {
      const response = await AddressService.getAddressByZipCode(cleanZip);
      setForm(prev => ({
        ...prev,
        zipCode: masked,
        street: response.street || prev.street,
        neighborhood: response.district || prev.neighborhood,
        city: response.city || prev.city,
        state: response.state ? String(response.state).toUpperCase() : prev.state
      }));
    } catch (error) {
      setAddressLookupError('CEP não encontrado. Verifique e tente novamente.');
    } finally {
      setAddressLookupLoading(false);
    }
  };

  const handleSubmit = () => {
    const nextErrors = validateForm(form, isBrokerAdmin);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      dangerNotification(
        'Validação',
        'Preencha corretamente os campos obrigatórios do colaborador.',
        6000
      );
      return;
    }

    onSubmit({
      ...form,
      estateId: selectedEstateId
    });
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon closeOnDimmerClick={false}>
      <Modal.Header>Adicionar colaborador</Modal.Header>
      <Modal.Content scrolling>
        <Form>
          <Grid>
            <Grid.Row>
              {canPickEstate && (
                <Grid.Column computer={8} tablet={16} mobile={16}>
                  <Form.Field error={Boolean(errors.estateId)}>
                    <label>Imobiliária *</label>
                    <Dropdown
                      fluid
                      search
                      selection
                      placeholder="Selecionar..."
                      value={form.estateId}
                      options={estateOptions}
                      onChange={(_, { value }) => updateField('estateId', String(value || ''))}
                    />
                    {errors.estateId && (
                      <span style={{ color: '#9f3a38', fontSize: 12 }}>
                        {errors.estateId}
                      </span>
                    )}
                  </Form.Field>
                </Grid.Column>
              )}

              <Grid.Column computer={canPickEstate ? 8 : 16} tablet={16} mobile={16}>
                <Form.Field error={Boolean(errors.userId)}>
                  <label>Usuário</label>
                  <Dropdown
                    fluid
                    search
                    selection
                    clearable
                    placeholder={
                      selectedEstateId ? 'Selecionar...' : 'Selecione uma imobiliária'
                    }
                    value={form.userId}
                    options={userOptions}
                    loading={usersLoading}
                    disabled={!selectedEstateId || usersLoading}
                    onChange={(_, { value }) => applySelectedUser(String(value || ''))}
                  />
                  {errors.userId && (
                    <span style={{ color: '#9f3a38', fontSize: 12 }}>
                      {errors.userId}
                    </span>
                  )}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={1}>
              <Grid.Column>
                <Header as="h4">Dados do recebedor</Header>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={4}>
              <Grid.Column computer={6} tablet={16} mobile={16}>
                <Form.Field error={Boolean(errors.name)}>
                  <label>Nome completo *</label>
                  <Input
                    value={form.name}
                    disabled={Boolean(form.userId)}
                    onChange={(_, { value }) => updateField('name', value)}
                  />
                  {errors.name && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.name}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.cpf)}>
                  <label>CPF *</label>
                  <Input
                    value={form.cpf}
                    onChange={(_, { value }) => updateField('cpf', maskCpf(value))}
                  />
                  {errors.cpf && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.cpf}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={6} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.email)}>
                  <label>E-mail *</label>
                  <Input
                    value={form.email}
                    disabled={Boolean(form.userId)}
                    onChange={(_, { value }) => updateField('email', value)}
                  />
                  {errors.email && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.email}</span>}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={4}>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field>
                  <label>Celular</label>
                  <Input
                    placeholder="(00) 00000-0000"
                    value={form.phone}
                    onChange={(_, { value }) => updateField('phone', maskPhoneNumber(value))}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.birthdate)}>
                  <label>Data de nascimento *</label>
                  <Input
                    type="date"
                    value={form.birthdate}
                    onChange={(_, { value }) => updateField('birthdate', value)}
                  />
                  {errors.birthdate && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.birthdate}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.monthlyIncome)}>
                  <label>Renda mensal *</label>
                  <Input
                    type="number"
                    min="0"
                    value={form.monthlyIncome}
                    onChange={(_, { value }) => updateField('monthlyIncome', value)}
                  />
                  {errors.monthlyIncome && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.monthlyIncome}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.professionalOccupation)}>
                  <label>Profissão *</label>
                  <Input
                    value={form.professionalOccupation}
                    onChange={(_, { value }) => updateField('professionalOccupation', value)}
                  />
                  {errors.professionalOccupation && (
                    <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.professionalOccupation}</span>
                  )}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={1}>
              <Grid.Column>
                <Header as="h4">Endereço</Header>
                {addressLookupError && <Message negative size="small">{addressLookupError}</Message>}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={4}>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.zipCode)}>
                  <label>CEP *</label>
                  <Input
                    value={form.zipCode}
                    loading={addressLookupLoading}
                    onChange={(_, { value }) => handleZipCodeLookup(value)}
                  />
                  {errors.zipCode && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.zipCode}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={6} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.street)}>
                  <label>Rua *</label>
                  <Input
                    value={form.street}
                    onChange={(_, { value }) => updateField('street', value)}
                  />
                  {errors.street && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.street}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.streetNumber)}>
                  <label>Número *</label>
                  <Input
                    value={form.streetNumber}
                    onChange={(_, { value }) => updateField('streetNumber', value)}
                  />
                  {errors.streetNumber && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.streetNumber}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.state)}>
                  <label>UF *</label>
                  <Input
                    maxLength={2}
                    value={form.state}
                    onChange={(_, { value }) => updateField('state', value.toUpperCase())}
                  />
                  {errors.state && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.state}</span>}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={4}>
              <Grid.Column computer={5} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.neighborhood)}>
                  <label>Bairro *</label>
                  <Input
                    value={form.neighborhood}
                    onChange={(_, { value }) => updateField('neighborhood', value)}
                  />
                  {errors.neighborhood && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.neighborhood}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={5} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.city)}>
                  <label>Cidade *</label>
                  <Input
                    value={form.city}
                    onChange={(_, { value }) => updateField('city', value)}
                  />
                  {errors.city && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.city}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Field>
                  <label>Complemento</label>
                  <Input
                    value={form.complementary}
                    onChange={(_, { value }) => updateField('complementary', value)}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.referencePoint)}>
                  <label>Referência</label>
                  <Input
                    value={form.referencePoint}
                    onChange={(_, { value }) => updateField('referencePoint', value)}
                  />
                  {errors.referencePoint && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.referencePoint}</span>}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={1}>
              <Grid.Column>
                <Header as="h4">Dados bancários</Header>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={4}>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.bankCode)}>
                  <label>Banco *</label>
                  <Input
                    value={form.bankCode}
                    onChange={(_, { value }) => updateField('bankCode', value.replace(/\D/g, ''))}
                  />
                  {errors.bankCode && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.bankCode}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.bankBranchNumber)}>
                  <label>Agência *</label>
                  <Input
                    value={form.bankBranchNumber}
                    onChange={(_, { value }) => updateField('bankBranchNumber', value.replace(/\D/g, ''))}
                  />
                  {errors.bankBranchNumber && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.bankBranchNumber}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Field>
                  <label>Dígito agência</label>
                  <Input
                    value={form.bankBranchCheckDigit}
                    onChange={(_, { value }) => updateField('bankBranchCheckDigit', value)}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={6} tablet={8} mobile={16}>
                <Form.Field>
                  <label>Tipo de conta *</label>
                  <Dropdown
                    fluid
                    selection
                    value={form.bankAccountType}
                    options={[
                      { key: 'checking', text: 'Conta corrente', value: 'checking' },
                      { key: 'savings', text: 'Conta poupança', value: 'savings' }
                    ]}
                    onChange={(_, { value }) =>
                      updateField(
                        'bankAccountType',
                        value as CreateCommissioningCollaboratorFormState['bankAccountType']
                      )
                    }
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={2}>
              <Grid.Column computer={8} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.bankAccountNumber)}>
                  <label>Conta *</label>
                  <Input
                    value={form.bankAccountNumber}
                    onChange={(_, { value }) => updateField('bankAccountNumber', value.replace(/\D/g, ''))}
                  />
                  {errors.bankAccountNumber && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.bankAccountNumber}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={8} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.bankAccountCheckDigit)}>
                  <label>Dígito da conta *</label>
                  <Input
                    value={form.bankAccountCheckDigit}
                    onChange={(_, { value }) => updateField('bankAccountCheckDigit', value)}
                  />
                  {errors.bankAccountCheckDigit && (
                    <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.bankAccountCheckDigit}</span>
                  )}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" text="Cancelar" light onClick={onClose} disabled={saving} />
        <Button
          type="button"
          text="Confirmar"
          loading={saving}
          disabled={saving}
          onClick={handleSubmit}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default CreateCollaboratorModal;
